import dynamic from "next/dynamic";
import Head from "next/head";

import LinkButton from "components/button/LinkButton";
import DetailedOfferCard from "components/cards/DetailedOfferCard";
import ModelOfferCard from "components/cards/ModelOfferCard";
import SeoLeasingInfo from "components/collapse/SeoLeasingInfo";
import Textlink from "components/link/Textlink";
import Checklist from "components/list/Checklist";
import BrandModelHero from "components/pages/[brand]/BrandModelHero";
import CollapseLeasingFAQ from "content/collapse/CollapseLeasingFAQ";
import BMW2erSeoComponent, {
  BMW2erHead,
} from "content/seo/components/BMW2er";
import NullLeasingInfos from "layout/NullLeasingInfos";
import Quickaccess from "layout/Quickaccess";
import SectionHowToNullLeasing from "layout/SectionHowToNullLeasing";
import SectionLeasingOfferQuickfinder from "layout/SectionLeasingOfferQuickfinder";
import SectionNewsletterSubscription from "layout/SectionNewsletterSubscription";
import { buildBrand } from "routes/buildBrands";
import { buildModel } from "routes/buildModels";
import buildModelTypes from "routes/buildModelTypes";
import brandConfigs, { findBrand } from "routes/configs/brands";
import modelConfigs from "routes/configs/models";
import buildBreadcrumbItems from "routes/helpers/buildBreadcrumbItems";
import buildModelSeoDescription from "routes/helpers/buildModelSeoDescription";
import buildFinderUrl from "utils/buildFinderUrl";
import buildQuickfinderData from "utils/buildQuickfinderData";
import fetchProducts from "utils/fetchProducts";
import buildCanonicalTagHref from "utils/seo/buildCanonicalTagHref";
import { transformProducts, transformQuickLinks } from "utils/transformators";

import config from "../../../../config";

const TrustpilotBox = dynamic(() => import("layout/TrustpilotBox"), { ssr: false });

// TODO: getSeoComponent function that works like getSeoContent in buildBrands/Models

function Model({
  brand,
  brandPageUrl,
  modelPageUrl,
  breadcrumbItems,
  model,
  heroImageBase,
  heroImagePosition,
  heroImageWithBackground,
  heroImageFullCover,
  heroTextInverted,
  minLeasingRate,
  cheapestProduct,
  quickLinks,
  quickfinderFilters,
  topOffers,
  topOffersUrl,
  modelTypes,
  seoContent,
  seoDescription,
  hasOffers,
}) {
  const isBMW2er = brand === "BMW" && model === "2er";

  return (
    <div>
      {isBMW2er ? (
        <BMW2erHead modelPageUrl={modelPageUrl} />
      ) : (
        <Head>
          <meta name="description" content={seoDescription} />
          <meta
            name="robots"
            content={config.robotsContent}
          />
          <link rel="canonical" href={buildCanonicalTagHref(modelPageUrl || "")} />
          <title>{`${brand} ${model} Leasing ohne Anzahlung | Angebote und Schnäppchen zu TOP Preisen`}</title>
        </Head>
      )}

      <section>
        <BrandModelHero
          brand={brand}
          brandPageUrl={brandPageUrl}
          model={model}
          breadcrumbItems={breadcrumbItems}
          heroImageBase={heroImageBase}
          heroImagePosition={heroImagePosition}
          heroImageWithBackground={heroImageWithBackground}
          heroImageFullCover={heroImageFullCover}
          heroTextInverted={heroTextInverted}
          minLeasingRate={minLeasingRate}
          cheapestProduct={cheapestProduct}
          hasOffers={hasOffers}
          showModelTypeSelect
          modelTypes={modelTypes}
          quickfinderFilters={quickfinderFilters}
        />
      </section>

      {hasOffers && (
        <section className="py-15 lg:pt-10 lg:pb-24" data-type-mapping="offer_grid">
          <div className="page-container px-5 mb-4 lg:mb-5">
            <h2 className="headline--6 uppercase">Top Angebote</h2>
            <p className="text-grey-darker">
              Ohne Anzahlung. Geprüfte Händler.
            </p>
          </div>
          <div className="page-container px-2 flex flex-wrap">
            {topOffers.map((offer) => (
              <DetailedOfferCard
                classNames={["w-full", "md:w-1/2", "lg:w-1/3"]}
                key={offer.offerUrl}
                {...offer}
              />
            ))}
          </div>
          <div className="page-container flex flex-col items-center mt-3 lg:mt-16 px-3">
            <LinkButton
              href={topOffersUrl || ""}
              useAnchor
              color="primary"
              large
              classNames={["md:max-w-none", "md:w-full", "lg:w-auto"]}
            >
              Weitere
              {" "}
              {brand}
              {" "}
              {model}
              {" "}
              Leasing Angebote anzeigen
            </LinkButton>
          </div>
        </section>
      )}

      {hasOffers && (
        <section className="hidden lg:block" data-type-mapping="generic_cards">
          <Quickaccess quickLinks={quickLinks} />
        </section>
      )}

      <div data-type-mapping="seo_text">
        {!!seoContent && <SeoLeasingInfo content={seoContent} />}

        <section className="py-16 lg:py-24 border-b">
          {isBMW2er ? (
            <BMW2erSeoComponent hasOffers={hasOffers} modelTypes={modelTypes} />
          ) : (
            <NullLeasingInfos />
          )}
        </section>
      </div>

      {hasOffers && modelTypes.length > 0 && !isBMW2er && (
        <section className="py-16 lg:py-24 border-b">
          <div className="page-container page-container--tight px-5 mb-10 lg:mb-20">
            <h2 className="headline--6 uppercase">Alle Fahrzeugtypen</h2>
          </div>
          <div className="page-container page-container--tight px-1 md:px-3 flex flex-wrap">
            {modelTypes.map(
              ({
                brand,
                imagePath,
                pageUrl,
                title,
                minLeasingRate,
                hasOffers,
                cheapestProduct,
              }) => (
                <ModelOfferCard
                  key={pageUrl}
                  brand={brand}
                  imagePath={imagePath}
                  pageUrl={pageUrl}
                  title={title}
                  minLeasingRate={minLeasingRate}
                  hasOffers={hasOffers}
                  cheapestProduct={cheapestProduct}
                />
              ),
            )}
          </div>
        </section>
      )}

      <SectionHowToNullLeasing />

      <SectionLeasingOfferQuickfinder
        brand={brand}
        model={hasOffers ? model : null}
        quickfinderFilters={quickfinderFilters}
      />

      <section className="border-b">
        <div className="mx-auto px-5 pt-16 lg:pt-24 mb-16 lg:mb-20 flex flex-col items-center ">
          <h2 className="headline--4 text-center px-5" data-type-mapping="seo_text">
            Unser kostenloser Service für Sie:
          </h2>
          <div className="mt-4">
            <Checklist
              list={[
                "Vermittlung zum Händler",
                "Unabhängiger Ansprechpartner",
                "Große Auswahl an sofort verfügbaren Leasingfahrzeugen in Deutschland",
              ]}
              classNames={["px-5", "lg:max-w-xs"]}
            />
          </div>
        </div>
        <div className="text-center mb-16 lg:mb-24" data-type-mapping="trustpilot">
          <TrustpilotBox />
        </div>
      </section>

      <SectionNewsletterSubscription />

      <section className="py-16 lg:py-24 border-b" data-type-mapping="question_and_answer">
        <div className="page-container page-container--tight">
          <h2 className="text-lg uppercase font-bold mb-4 px-5">Leasing FAQ</h2>
          <CollapseLeasingFAQ />
          <div className="mt-6 px-5">
            <Textlink to="/faq" color="primary">
              Alle FAQ ansehen
            </Textlink>
          </div>
        </div>
      </section>

      <section className="py-10 border-b">
        <div className="text-center" data-type-mapping="trustpilot">
          <TrustpilotBox />
        </div>
      </section>
    </div>
  );
}

export async function getStaticPaths() {
  const paths = [];

  brandConfigs.forEach(({ brand, pageUrl }) => {
    const models = modelConfigs[brand.toLowerCase()] || [];
    models.forEach(model => {
      paths.push({ params: { brand: pageUrl.replace("/", ""), model: model.modelSlug } });
    });
  });

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps(context) {
  const brandSettings = findBrand(context.params.brand);
  const brandConfig = await buildBrand(brandSettings);

  const {
    brand,
    pageUrl: brandPageUrl,
  } = brandConfig;

  const brandData = await fetchProducts(
    { marke: brand },
    { _size: 12, _top: true },
  );

  const modelConfig = await buildModel(brandConfig, brandData.filters.modell, context.params.model);

  const {
    model,
    pageUrl: modelPageUrl,
    heroImagePosition,
    heroImageWithBackground,
    heroImageFullCover,
    heroTextInverted,
    title,
    minLeasingRate,
    heroImageBase,
    cheapestProduct,
    seoContent,
    hasOffers,
  } = modelConfig;

  const topOffers = await fetchProducts(
    { marke: brand, modell: model },
    { _size: 3, _top: true },
  );

  const buildLeasingRates = (ratesFilter) => ratesFilter.slice(0, 6);

  return ({
    props: {
      brand,
      brandPageUrl,
      model,
      heroImageBase: heroImageBase || null,
      heroImagePosition: heroImagePosition || null,
      heroImageWithBackground: heroImageWithBackground || null,
      heroImageFullCover: heroImageFullCover || null,
      heroTextInverted: heroTextInverted || null,
      modelPageUrl,
      minLeasingRate: minLeasingRate || null,
      cheapestProduct: cheapestProduct || null,
      breadcrumbItems: buildBreadcrumbItems({
        brand,
        brandPageUrl,
        model,
        modelPageUrl,
      }),
      seoContent,
      seoDescription: buildModelSeoDescription(title, minLeasingRate || null),
      hasOffers,
      topOffers: transformProducts(topOffers.products, "detailed"),
      topOffersUrl: buildFinderUrl({ marke: brand, modell: model }),
      quickLinks: transformQuickLinks(
        buildLeasingRates(topOffers.filters["leasing.rate_lte"]),
        brand,
        model,
      ),
      quickfinderFilters: buildQuickfinderData(topOffers),
      modelTypes: await buildModelTypes(brand, model),
    },
    revalidate: 600, // In seconds
  });
}

export default Model;
